'use client';

import type { ComponentType } from 'react';
import { useEffect, useState } from 'react';

import { useCurrentUser } from '@website/hooks/useCurrentUser';

import type { CacheRolloutBannerImplProps } from './CacheRolloutBannerImpl';

interface CacheRolloutBannerProps {
  /** Which entity the banner is talking about — drives translations + the optional preview link. */
  entityType: 'activity' | 'professional';
  /** Activity id, or professional id. Passed to the cache-info endpoint. */
  entityId: string;
  /**
   * The professional id who owns this entity. For activities this is
   * `activity.professionalId`; for professionals it's the entity id itself.
   * Used to decide whether the viewer is the owner.
   */
  ownerProfessionalId: string;
}

/**
 * Owner / admin-only banner shown for ~2 minutes after a static-content change
 * (revision approved, no-review edit, slot change, etc.) so the
 * professional knows the CDN-cached page they're looking at may still be
 * serving the previous version.
 *
 * Shell pattern — anonymous viewers and non-owners hit `return null` before
 * the dynamic import fires, so the React Query + service code stays off the
 * public detail page's bundle graph.
 *
 * Owner detection uses `user.id === ownerProfessionalId`; admins (`user.isAdmin`)
 * also see the banner so they can verify a change they approved is rolling out.
 */
export default function CacheRolloutBanner({ entityType, entityId, ownerProfessionalId }: CacheRolloutBannerProps) {
  const { data: user } = useCurrentUser();
  const [Impl, setImpl] = useState<ComponentType<CacheRolloutBannerImplProps> | null>(null);

  const isOwner = !!user && user.isProfessional === true && user.id === ownerProfessionalId;
  const isAdmin = !!user?.isAdmin;
  const shouldLoad = isOwner || isAdmin;

  useEffect(() => {
    if (!shouldLoad || Impl) return;
    void import(/* webpackPrefetch: false, webpackPreload: false */ './CacheRolloutBannerImpl').then((m) =>
      setImpl(() => m.default)
    );
  }, [shouldLoad, Impl]);

  if (!shouldLoad || !Impl) return null;
  return <Impl entityType={entityType} entityId={entityId} />;
}
